import React, { useContext } from 'react';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { Paper, Typography, Stack, Divider, Chip, useTheme, CustomTheme, Box, Tooltip } from '@mui/material';
import { AddCircleOutline as AddCircleOutlineIcon, Circle as CircleIcon } from '@mui/icons-material';
import { useMutation, useQuery } from '@apollo/client';
import dayjs from 'op-utils/dayjs';
import ReviewCard from './ReviewCard';
import { CREATE_TREATMENT_REVIEW, GET_TREATMENT_REVIEW_LIST } from './Queries';
import { useState } from 'react';
import { DoseSiteSummaryType, TreatmentReviewType } from './Interface';
import { TenantContext, TenantContextType } from 'op-contexts/TenantContext/TenantContext';
import { CurrentAppConfig } from '../Careplan/AppConfig';

const Status = ({ status, ...props }: { status: string }): JSX.Element => {
  const theme: CustomTheme = useTheme<CustomTheme>();
  const borderColourMap: { [status: string]: string } = {
    Draft: theme.palette.statusColors.draft,
    'Unsubmitted changes': theme.palette.warning.main,
    Submitted: theme.palette.success.main,
  };
  const textColourMap: { [status: string]: string } = {
    Draft: theme.palette.statusContrastText.draft,
    'Unsubmitted changes': theme.palette.warning.dark,
    Submitted: theme.palette.success.dark,
  };

  return (
    <Chip
      label={status}
      sx={{ '&.MuiChip-root': { borderColor: borderColourMap[status], color: textColourMap[status] } }}
      variant="outlined"
      size="small"
    />
  );
};

const LandingPage = (): JSX.Element => {
  const match: { params: { id: string } } = useRouteMatch();
  const { id: patientId } = match.params;
  const history = useHistory();
  const theme: CustomTheme = useTheme<CustomTheme>();
  const [creatingReview, setCreatingReview] = useState(false);
  const { data: TreatmentReviewData, loading: TreatmentReviewLoading } = useQuery(GET_TREATMENT_REVIEW_LIST, {
    variables: { patientId },
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'network-only',
    skip: !patientId,
  });
  const [createTreatmentReview] = useMutation(CREATE_TREATMENT_REVIEW);
  const { doseUnit, fractionUnit } = useContext<TenantContextType>(TenantContext);
  const handleAddReview = () => {
    if (creatingReview) return;
    setCreatingReview(true);
    createTreatmentReview({
      variables: {
        patientId,
      },
    })
      .then((res) => {
        history.push(
          `/radiation/patient/${patientId}/treatment-review/${res.data.createTreatmentReview.review.group.id}`,
        );
      })
      .finally(() => {
        setCreatingReview(false);
      });
  };
  const handleExistingReview = (groupId: string) => {
    history.push(`/radiation/patient/${patientId}/treatment-review/${groupId}`);
  };

  if (TreatmentReviewLoading) return <p>Loading...</p>;

  return (
    <Paper
      sx={{ width: 1, height: 1, marginX: 2, minHeight: 0, display: 'flex', flexDirection: 'column' }}
      elevation={10}>
      <Stack
        direction="column"
        spacing={2}
        sx={{ minHeight: 0, flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', padding: 2 }}>
        <Typography variant="h6" lineHeight="28px">
          Treatment Review
        </Typography>
        <Divider sx={{ margin: '16px -16px 0px -16px !important' }} />
        <Stack direction="row" gap={1} flexWrap="wrap" sx={{ overflowY: 'auto', overflowX: 'hidden' }}>
          <ReviewCard
            variant="outlined"
            onClick={() => handleAddReview()}
            aria-disabled={creatingReview}
            data-testid="add-review">
            <Stack direction="row" spacing={1} alignItems="center">
              <AddCircleOutlineIcon fontSize="small" sx={{ color: theme.palette.grey[600] }} />
              <Typography fontSize={16}>ADD TREATMENT REVIEW</Typography>
            </Stack>
          </ReviewCard>
          {TreatmentReviewData?.treatmentReviewList?.map((review: TreatmentReviewType) => {
            const dssTitle =
              review.dosesitesummarySet
                ?.filter((dss: DoseSiteSummaryType) => dss.isSelected)
                .map((dss: DoseSiteSummaryType) => dss.site)
                .join(', ') || 'No DSS selected';
            const dssSubtitle = review.dosesitesummarySet
              ?.filter((dss: DoseSiteSummaryType) => dss.isSelected)
              .map(
                (dss: DoseSiteSummaryType) =>
                  `${dss.deliveredDosage}${doseUnit}/${dss.deliveredFractionDose}${fractionUnit}`,
              )
              .join(', ');
            const tooltipTitle = review.dosesitesummarySet
              ?.filter((dss: DoseSiteSummaryType) => dss.isSelected)
              .map((dss: DoseSiteSummaryType) => (
                <p style={{ whiteSpace: 'nowrap', marginBottom: '0px' }}>
                  {dss.site} | Delivered: {dss.deliveredDosage}
                  {doseUnit}/{dss.deliveredFractionDose}
                  {fractionUnit}
                </p>
              ));
            return (
              <ReviewCard
                key={review.id}
                onClick={() => handleExistingReview(review.group.id)}
                aria-disabled={creatingReview}
                data-testid={`existing-review-${review.id}`}>
                <Stack direction="column" spacing={1}>
                  <Tooltip
                    placement="top"
                    arrow
                    slotProps={{
                      tooltip: {
                        sx: {
                          backgroundColor: theme.palette.primary.light,
                          color: theme.palette.primary.contrastText,
                        },
                      },
                      arrow: {
                        sx: {
                          color: theme.palette.primary.light,
                        },
                      },
                      popper: {
                        modifiers: [
                          {
                            name: 'offset',
                            options: {
                              offset: [0, -8],
                            },
                          },
                        ],
                      },
                    }}
                    title={
                      tooltipTitle ? (
                        <Typography variant="body2" color={theme.palette.primary.contrastText}>
                          Treatment Sites: {tooltipTitle}
                        </Typography>
                      ) : (
                        ''
                      )
                    }>
                    <Stack direction="column">
                      <Typography
                        variant="subtitle1"
                        sx={{ textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>
                        {dssTitle || 'No DSS selected'}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>
                        {dssSubtitle ? `Delivered: ${dssSubtitle}` : '-'}
                      </Typography>
                    </Stack>
                  </Tooltip>
                  <Divider />
                  <Stack direction="column">
                    <Typography variant="body2" color="text.primary">
                      Review date: {dayjs(review.reviewDate).format(CurrentAppConfig.OTRPage.DateFormat)}
                    </Typography>
                    <Typography variant="body2" color="text.primary" sx={{ paddingBottom: '8px' }}>
                      Toxicity assessment: {review.toxicityType || '-'}
                    </Typography>
                    <Box>
                      <Status status={review.status} />
                    </Box>
                  </Stack>
                </Stack>
              </ReviewCard>
            );
          })}
        </Stack>
      </Stack>
    </Paper>
  );
};

export default LandingPage;
